import React, { useState, useEffect } from "react";
import { Cap, Level } from "../utils/rbacCatalog";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Select, TextInput } from "flowbite-react";
import { Spinner } from "../components/atoms/Spinner";
import moment from "moment";
import {
  HiCurrencyDollar,
  HiLightningBolt,
  HiCreditCard,
  HiOfficeBuilding,
  HiCheck,
  HiExclamation,
  HiPencil,
  HiTrash,
  HiPlus,
  HiShieldCheck,
  HiLink,
  HiGlobe,
  HiBadgeCheck,
  HiChartBar,
} from "react-icons/hi";
import { SiStripe, SiBitcoin } from "react-icons/si";
import useAuth from "../hooks/useAuth";
import { useGetEntityUser } from "../hooks/users";
import { useGetEntity } from "../hooks/entity";
import { usePermissions, PlanTier } from "../utils/permissions";
import PaymentModuleService, {
  PayoutAccountResponseDTO,
  PayoutMethodEnum,
  PayoutMethod,
  BankTransferAccountDetail,
  CryptoAccountDetail,
  BalanceResponseDTO,
  PayoutResponseDTO,
  PlanRegistrationResponseDTO,
  RecurrencyEnum,
  PaymentRequestCurrencyEnum,
  FeeCalculationResponseDTO,
} from "../services/paymentModule";
import { formatPayModDate } from "../services/planChange";
import { toast } from "react-toastify";
import { CustomButton } from "../components/atoms/CustomButton";
import { ModalShell } from "../components/atoms/ModalShell";
import { StatusBadge, BadgeTone } from "../components/atoms/StatusBadge";
import { UnlockBadge } from "../components/atoms/UnlockBadge";
import { FieldLabel } from "../components/atoms/FieldLabel";
import {
  FeatureBenefitsCard,
  FeatureBenefit,
} from "../components/atoms/FeatureBenefitsCard";
import { PlansModal } from "../components/modals/PlansModal";
import { countries } from "../utils/countries";
import { momentLocaleOf } from "../utils/momentLocale";

const STRIPE_ALLOWED_COUNTRIES = [
  "CH",
  "US",
  "GB",
  "CA",
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IS",
  "IE",
  "IT",
  "LV",
  "LI",
  "LT",
  "LU",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

interface PayoutMethodOption {
  method: PayoutMethodEnum;
  title: string;
  description: string;
  feeRate: string;
  icon: React.ReactNode;
  recommended?: boolean;
  requiresUpgrade?: boolean;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  PAID: "success",
  ACTIVE: "success",
  PENDING: "warning",
  RESTRICTED: "warning",
  PROCESSING: "info",
  FAILED: "failure",
  REJECTED: "failure",
  DISABLED: "failure",
  INACTIVE: "gray",
};

const statusTone = (status: string): BadgeTone =>
  STATUS_TONE[status?.toUpperCase()] || "gray";

const PaymentModule: React.FC = () => {
  const { user, customer_identification } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const permissions = usePermissions(user);

  moment.locale(momentLocaleOf(i18n.language));

  const hasAdvancedPayoutAccess = permissions.isAtLeastTier(
    PlanTier.LARGE_BUSINESS
  );

  const payoutMethodOptions: PayoutMethodOption[] = [
    {
      method: PayoutMethod.STRIPE_CONNECT,
      title: t("paymentModule.payoutMethods.stripeConnect.title"),
      description: t("paymentModule.payoutMethods.stripeConnect.description"),
      feeRate: t("paymentModule.payoutMethods.stripeConnect.feeRate"),
      icon: <SiStripe size={24} />,
      recommended: true,
    },
    {
      method: PayoutMethod.BANK_TRANSFER,
      title: t("paymentModule.payoutMethods.bankTransfer.title"),
      description: t("paymentModule.payoutMethods.bankTransfer.description"),
      feeRate: t("paymentModule.payoutMethods.bankTransfer.feeRate"),
      icon: <HiOfficeBuilding size={24} />,
      requiresUpgrade: !hasAdvancedPayoutAccess,
    },
    {
      method: PayoutMethod.CRYPTO,
      title: t("paymentModule.payoutMethods.crypto.title"),
      description: `${t(
        "paymentModule.payoutMethods.crypto.description"
      )} | ${t("paymentModule.accounts.cryptoLimit")}`,
      feeRate: t("paymentModule.payoutMethods.crypto.feeRate"),
      icon: <SiBitcoin size={24} />,
      requiresUpgrade: !hasAdvancedPayoutAccess,
    },
  ];

  const paymentLinksBenefits: FeatureBenefit[] = [
    {
      label: t("paymentModule.paymentLinksPromo.security"),
      icon: <HiShieldCheck className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
    {
      label: t("paymentModule.paymentLinksPromo.fraud"),
      icon: <HiLightningBolt className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
    {
      label: t("paymentModule.paymentLinksPromo.secure3d"),
      icon: <HiBadgeCheck className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
    {
      label: t("paymentModule.paymentLinksPromo.chargeback"),
      icon: <HiCreditCard className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
    {
      label: t("paymentModule.paymentLinksPromo.global"),
      icon: <HiGlobe className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
    {
      label: t("paymentModule.paymentLinksPromo.riskScoring"),
      icon: <HiChartBar className="text-gray-500" size={14} />,
      iconBgClass: "bg-gray-100",
    },
  ];

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<PayoutAccountResponseDTO[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PayoutMethodEnum | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showBankModal, setShowBankModal] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [showCountryWarningModal, setShowCountryWarningModal] = useState(false);

  const [bankForm, setBankForm] = useState<BankTransferAccountDetail>({
    account_holder: "",
    iban: "",
    bic: "",
    bank_name: "",
    country: "",
    bank_address: "",
  });

  // Crypto form state - fixed to Ethereum/USDC (ERC-20)
  const [walletAddress, setWalletAddress] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [isStripeOnboarding, setIsStripeOnboarding] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingMethod, setDeletingMethod] = useState<PayoutMethodEnum | null>(
    null
  );

  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [addingMethod, setAddingMethod] = useState<PayoutMethodEnum | null>(
    null
  );

  const [recurringPlans, setRecurringPlans] = useState<
    PlanRegistrationResponseDTO[]
  >([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [deletingPlanCode, setDeletingPlanCode] = useState<string | null>(null);
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<{
    planCode: string;
    description: string;
  } | null>(null);
  const [editPlanDescription, setEditPlanDescription] = useState("");
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editPlanTouched, setEditPlanTouched] = useState(false);
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    amount: "",
    currency: "USD" as PaymentRequestCurrencyEnum,
    recurrency: "MONTHLY" as RecurrencyEnum,
    description: "",
  });
  const [planFormTouched, setPlanFormTouched] = useState({
    description: false,
    amount: false,
  });
  const [serviceDeclared, setServiceDeclared] = useState<boolean | null>(null);

  const [feeSimAmount, setFeeSimAmount] = useState("100");
  const [feeSimCurrency, setFeeSimCurrency] = useState("CHF");
  const [feeSimMethod, setFeeSimMethod] = useState("STRIPE_CONNECT");
  const [feeResult, setFeeResult] = useState<FeeCalculationResponseDTO | null>(
    null
  );
  const [loadingFees, setLoadingFees] = useState(false);
  const [showFeeSimModal, setShowFeeSimModal] = useState(false);

  const [balance, setBalance] = useState<BalanceResponseDTO | null>(null);
  const [payouts, setPayouts] = useState<PayoutResponseDTO[]>([]);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [showRequestPayoutModal, setShowRequestPayoutModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const { data: ownerProfile, isLoading: isProfileLoading } = useGetEntityUser({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    user_identification: customer_identification,
  });

  const ownerCountryCode = ownerProfile?.user_country_code || "";
  const isStripeCountryAllowed =
    STRIPE_ALLOWED_COUNTRIES.includes(ownerCountryCode);
  const ownerCountryName =
    countries.find((c) => c.code === ownerCountryCode)?.name ||
    ownerCountryCode;

  const { data: entityData } = useGetEntity(
    customer_identification,
    user.entity_identification
  );

  useEffect(() => {
    if (entityData?.entity_name) {
      PaymentModuleService.updateServiceDeclaration(
        customer_identification,
        user.entity_identification,
        {
          service_name: entityData.entity_name,
          service_description: entityData.entity_description || "",
        }
      ).catch(() => {});
    }
  }, [
    entityData?.entity_name,
    entityData?.entity_description,
    customer_identification,
    user.entity_identification,
  ]);

  useEffect(() => {
    fetchPayoutAccounts();
  }, [customer_identification, user.entity_identification]);

  useEffect(() => {
    if (accounts.length > 0) {
      fetchBalance();
      fetchPayouts();
    }
  }, [accounts.length]);

  const fetchBalance = async () => {
    try {
      setLoadingBalance(true);
      const response = await PaymentModuleService.getEntityPendingBalance(
        customer_identification,
        user.entity_identification
      );
      setBalance(response.data?.element || null);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error("Failed to fetch balance:", err);
      }
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchPayouts = async () => {
    try {
      setLoadingPayouts(true);
      const response = await PaymentModuleService.getEntityPayouts(
        customer_identification,
        user.entity_identification
      );
      setPayouts(response.data?.element || []);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error("Failed to fetch payouts:", err);
      }
      setPayouts([]);
    } finally {
      setLoadingPayouts(false);
    }
  };

  useEffect(() => {
    if (accounts.length > 0) {
      fetchRecurringPlans();
    }
  }, [accounts.length]);

  const fetchRecurringPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await PaymentModuleService.getRecurringPlans(
        customer_identification,
        user.entity_identification
      );
      const allPlans = response.data?.element || [];
      setRecurringPlans(
        allPlans.filter(
          (p: PlanRegistrationResponseDTO) => p.recurrency !== "UNIQUE"
        )
      );
      setServiceDeclared(true);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setServiceDeclared(false);
      }
      setRecurringPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const ensureServiceDeclared = async (): Promise<boolean> => {
    if (serviceDeclared) return true;
    try {
      await PaymentModuleService.declareService(
        customer_identification,
        user.entity_identification
      );
      setServiceDeclared(true);
      return true;
    } catch (err: any) {
      if (err.response?.status === 409) {
        setServiceDeclared(true);
        return true;
      }
      console.error("Failed to declare service:", err);
      toast.error(t("paymentModule.toasts.initFailed"));
      return false;
    }
  };

  const handleCreateRecurringPlan = async () => {
    setPlanFormTouched({ description: true, amount: true });

    const amountNum = parseFloat(planForm.amount);
    if (!planForm.description || planForm.description.length > 150) {
      return;
    }
    if (isNaN(amountNum) || amountNum < 1 || amountNum > 10000) {
      return;
    }

    try {
      setIsSubmitting(true);

      const serviceReady = await ensureServiceDeclared();
      if (!serviceReady) return;

      const amountInCents = Math.round(amountNum * 100);
      await PaymentModuleService.registerRecurringPlan(
        customer_identification,
        user.entity_identification,
        {
          amount: amountInCents,
          currency: planForm.currency,
          recurrency: planForm.recurrency,
          description: planForm.description,
        }
      );
      toast.success(t("paymentModule.toasts.planCreated"));
      await fetchRecurringPlans();
      setShowCreatePlanModal(false);
      setPlanForm({
        amount: "",
        currency: "USD",
        recurrency: "MONTHLY",
        description: "",
      });
      setPlanFormTouched({ description: false, amount: false });
    } catch (err: any) {
      if (err.response?.status === 402) {
        toast.error(t("paymentModule.toasts.createPlanUpgradeRequired"));
      } else {
        toast.error(
          err.response?.data?.message ||
            t("paymentModule.toasts.createPlanFailed")
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRecurringPlan = async () => {
    if (!editingPlan) return;
    setEditPlanTouched(true);

    if (
      !editPlanDescription.trim() ||
      editPlanDescription.trim().length > 150
    ) {
      return;
    }

    try {
      setIsEditingPlan(true);
      await PaymentModuleService.updateRecurringPlan(
        customer_identification,
        user.entity_identification,
        editingPlan.planCode,
        { description: editPlanDescription.trim() }
      );
      toast.success(t("paymentModule.toasts.planUpdated"));
      setRecurringPlans((prev) =>
        prev.map((p) =>
          p.plan_code === editingPlan.planCode
            ? { ...p, description: editPlanDescription.trim() }
            : p
        )
      );
      setEditingPlan(null);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.updatePlanFailed")
      );
    } finally {
      setIsEditingPlan(false);
    }
  };

  const handleDeleteRecurringPlan = async () => {
    if (!deletingPlanCode) return;
    try {
      setIsDeletingPlan(true);
      await PaymentModuleService.deleteRecurringPlan(
        customer_identification,
        user.entity_identification,
        deletingPlanCode
      );
      toast.success(t("paymentModule.toasts.planDeleted"));
      setRecurringPlans((prev) =>
        prev.filter((p) => p.plan_code !== deletingPlanCode)
      );
      setShowDeletePlanModal(false);
      setDeletingPlanCode(null);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.deletePlanFailed")
      );
    } finally {
      setIsDeletingPlan(false);
    }
  };

  const handleCalculateFees = async () => {
    const amountNum = parseFloat(feeSimAmount);
    if (isNaN(amountNum) || amountNum < 1 || amountNum > 100000) {
      toast.error(t("paymentModule.toasts.amountOutOfRange"));
      return;
    }
    const amountInCents = Math.round(amountNum * 100);

    try {
      setLoadingFees(true);
      const response = await PaymentModuleService.calculatePayoutFee(
        customer_identification,
        user.entity_identification,
        feeSimMethod,
        amountInCents,
        feeSimCurrency
      );
      setFeeResult(response.data?.element || null);
    } catch (err: any) {
      toast.error(t("paymentModule.toasts.feeCalculationFailed"));
      setFeeResult(null);
    } finally {
      setLoadingFees(false);
    }
  };

  const handleRequestPayout = async () => {
    try {
      setIsSubmitting(true);
      await PaymentModuleService.requestPayout(
        customer_identification,
        user.entity_identification
      );
      toast.success(t("paymentModule.toasts.payoutRequested"));
      setShowRequestPayoutModal(false);
      fetchBalance();
      fetchPayouts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.payoutRequestFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const accountStatusLabel = (status: string) => {
    const key = status?.toLowerCase();
    const label = t(`paymentModule.accounts.statusLabel.${key}`, {
      defaultValue: "",
    }) as string;
    return label || (t(`paymentModule.accounts.${key}`) as string) || status;
  };

  const payoutStatusLabel = (status: string) => {
    const key = status?.toLowerCase();
    const label = t(`paymentModule.payoutHistory.statusLabel.${key}`, {
      defaultValue: "",
    }) as string;
    return (
      label ||
      (t(`paymentModule.payoutHistory.status.${key}`) as string) ||
      status
    );
  };

  const canRequestPayout =
    balance?.balances?.some((b) => b.gross_amount > 0) ?? false;

  const fetchPayoutAccounts = async () => {
    try {
      setLoading(true);
      const response = await PaymentModuleService.getPayoutAccounts(
        customer_identification,
        user.entity_identification
      );
      setAccounts(response.data?.element || []);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error("Failed to fetch payout accounts:", err);
      }
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMethod = (method: PayoutMethodEnum) => {
    setSelectedMethod(method);
  };

  const handleStripeOnboarding = async () => {
    try {
      setIsStripeOnboarding(true);
      await ensureServiceDeclared();
      const response = await PaymentModuleService.getStripeOnboardingLink(
        customer_identification,
        user.entity_identification
      );
      const onboardingUrl = response.data?.element?.onboarding_url;
      if (onboardingUrl) {
        window.location.href = onboardingUrl;
      } else {
        toast.error(t("paymentModule.toasts.stripeOnboardingFailed"));
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.stripeOnboardingError")
      );
    } finally {
      setIsStripeOnboarding(false);
    }
  };

  const handleStripeOnboardingRefresh = async () => {
    try {
      setIsStripeOnboarding(true);
      const response =
        await PaymentModuleService.getStripeOnboardingRefreshLink(
          customer_identification,
          user.entity_identification
        );
      const onboardingUrl = response.data?.element?.onboarding_url;
      if (onboardingUrl) {
        window.location.href = onboardingUrl;
      } else {
        toast.error(t("paymentModule.toasts.stripeOnboardingFailed"));
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.stripeOnboardingError")
      );
    } finally {
      setIsStripeOnboarding(false);
    }
  };

  const handleContinueSetup = () => {
    if (selectedMethod === PayoutMethod.STRIPE_CONNECT) {
      handleStripeOnboarding();
    } else if (selectedMethod === PayoutMethod.BANK_TRANSFER) {
      setShowBankModal(true);
    } else if (selectedMethod === PayoutMethod.CRYPTO) {
      setShowCryptoModal(true);
    }
  };

  const handleBankFormChange = (
    field: keyof BankTransferAccountDetail,
    value: string
  ) => {
    setBankForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateBankForm = (): boolean => {
    if (!bankForm.account_holder.trim()) {
      toast.error(t("paymentModule.toasts.accountHolderRequired"));
      return false;
    }
    if (bankForm.account_holder.trim().length < 2) {
      toast.error(t("paymentModule.toasts.accountHolderTooShort"));
      return false;
    }
    if (bankForm.account_holder.trim().length > 150) {
      toast.error(t("paymentModule.toasts.accountHolderTooLong"));
      return false;
    }

    if (!bankForm.iban.trim()) {
      toast.error(t("paymentModule.toasts.ibanRequired"));
      return false;
    }
    const cleanIban = bankForm.iban.replace(/\s/g, "");
    if (cleanIban.length < 8) {
      toast.error(t("paymentModule.toasts.ibanTooShort"));
      return false;
    }
    if (cleanIban.length > 34) {
      toast.error(t("paymentModule.toasts.ibanTooLong"));
      return false;
    }

    if (bankForm.bic && bankForm.bic.trim()) {
      if (bankForm.bic.trim().length < 6) {
        toast.error(t("paymentModule.toasts.bicTooShort"));
        return false;
      }
      if (bankForm.bic.trim().length > 15) {
        toast.error(t("paymentModule.toasts.bicTooLong"));
        return false;
      }
    }

    if (bankForm.bank_name && bankForm.bank_name.trim()) {
      if (bankForm.bank_name.trim().length < 2) {
        toast.error(t("paymentModule.toasts.bankNameTooShort"));
        return false;
      }
      if (bankForm.bank_name.trim().length > 150) {
        toast.error(t("paymentModule.toasts.bankNameTooLong"));
        return false;
      }
    }

    if (bankForm.country && bankForm.country.trim()) {
      if (bankForm.country.trim().length < 2) {
        toast.error(t("paymentModule.toasts.countryTooShort"));
        return false;
      }
      if (bankForm.country.trim().length > 3) {
        toast.error(t("paymentModule.toasts.countryInvalidLength"));
        return false;
      }
    }

    if (bankForm.bank_address && bankForm.bank_address.trim()) {
      if (bankForm.bank_address.trim().length < 5) {
        toast.error(t("paymentModule.toasts.bankAddressTooShort"));
        return false;
      }
      if (bankForm.bank_address.trim().length > 200) {
        toast.error(t("paymentModule.toasts.bankAddressTooLong"));
        return false;
      }
    }

    return true;
  };

  const validateCryptoForm = (): boolean => {
    if (!walletAddress.trim()) {
      toast.error(t("paymentModule.toasts.walletAddressRequired"));
      return false;
    }
    const cleanWalletAddress = walletAddress.trim();
    if (cleanWalletAddress.length < 20) {
      toast.error(t("paymentModule.toasts.walletAddressTooShort"));
      return false;
    }
    if (cleanWalletAddress.length > 150) {
      toast.error(t("paymentModule.toasts.walletAddressTooLong"));
      return false;
    }
    // USDC is taken on Ethereum (ERC-20): the address must be 0x + 40 hex chars.
    if (!/^0x[a-fA-F0-9]{40}$/.test(cleanWalletAddress)) {
      toast.error(t("paymentModule.toasts.walletAddressInvalidFormat"));
      return false;
    }
    return true;
  };

  const handleSubmitBankAccount = async () => {
    if (!validateBankForm()) return;

    try {
      setIsSubmitting(true);
      await ensureServiceDeclared();
      await PaymentModuleService.createPayoutAccount(
        customer_identification,
        user.entity_identification,
        {
          payout_method: PayoutMethod.BANK_TRANSFER,
          account_detail: {
            ...bankForm,
            iban: bankForm.iban.replace(/\s/g, "").toUpperCase(),
          },
        }
      );
      toast.success(t("paymentModule.toasts.bankAccountAdded"));
      setShowBankModal(false);
      setBankForm({
        account_holder: "",
        iban: "",
        bic: "",
        bank_name: "",
        country: "",
        bank_address: "",
      });
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.bankAccountAddFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitCryptoAccount = async () => {
    if (!validateCryptoForm()) return;

    try {
      setIsSubmitting(true);
      await ensureServiceDeclared();
      await PaymentModuleService.createPayoutAccount(
        customer_identification,
        user.entity_identification,
        {
          payout_method: PayoutMethod.CRYPTO,
          account_detail: {
            wallet_address: walletAddress,
            network: "ethereum",
            currency: "USDC",
          },
        }
      );
      toast.success(t("paymentModule.toasts.cryptoWalletAdded"));
      setShowCryptoModal(false);
      setWalletAddress("");
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.cryptoWalletAddFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditAccount = (account: PayoutAccountResponseDTO) => {
    setIsEditMode(true);

    if (account.payout_method === PayoutMethod.BANK_TRANSFER) {
      const bankDetail = account.account_detail as BankTransferAccountDetail;
      setBankForm({
        account_holder: bankDetail.account_holder || "",
        iban: bankDetail.iban || "",
        bic: bankDetail.bic || "",
        bank_name: bankDetail.bank_name || "",
        country: bankDetail.country || "",
        bank_address: bankDetail.bank_address || "",
      });
      setShowBankModal(true);
    } else if (account.payout_method === PayoutMethod.CRYPTO) {
      const cryptoDetail = account.account_detail as CryptoAccountDetail;
      setWalletAddress(cryptoDetail.wallet_address || "");
      setShowCryptoModal(true);
    }
  };

  const handleUpdateBankAccount = async () => {
    if (!validateBankForm()) return;

    try {
      setIsSubmitting(true);
      await PaymentModuleService.updatePayoutAccount(
        customer_identification,
        user.entity_identification,
        PayoutMethod.BANK_TRANSFER,
        {
          payout_method: PayoutMethod.BANK_TRANSFER,
          account_detail: {
            ...bankForm,
            iban: bankForm.iban.replace(/\s/g, "").toUpperCase(),
          },
        }
      );
      toast.success(t("paymentModule.toasts.bankAccountUpdated"));
      setShowBankModal(false);
      setIsEditMode(false);
      setBankForm({
        account_holder: "",
        iban: "",
        bic: "",
        bank_name: "",
        country: "",
        bank_address: "",
      });
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.bankAccountUpdateFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCryptoAccount = async () => {
    if (!validateCryptoForm()) return;

    try {
      setIsSubmitting(true);
      await PaymentModuleService.updatePayoutAccount(
        customer_identification,
        user.entity_identification,
        PayoutMethod.CRYPTO,
        {
          payout_method: PayoutMethod.CRYPTO,
          account_detail: {
            wallet_address: walletAddress,
            network: "ethereum",
            currency: "USDC",
          },
        }
      );
      toast.success(t("paymentModule.toasts.cryptoWalletUpdated"));
      setShowCryptoModal(false);
      setIsEditMode(false);
      setWalletAddress("");
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.cryptoWalletUpdateFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletingMethod) return;

    try {
      setIsSubmitting(true);
      await PaymentModuleService.deletePayoutAccount(
        customer_identification,
        user.entity_identification,
        deletingMethod
      );
      const methodName =
        payoutMethodOptions.find((m) => m.method === deletingMethod)?.title ||
        deletingMethod;
      toast.success(
        `${methodName} ${t("paymentModule.toasts.accountDeleted")}`
      );
      setShowDeleteModal(false);
      setDeletingMethod(null);
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.accountDeleteFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (method: PayoutMethodEnum) => {
    try {
      setIsSubmitting(true);
      await PaymentModuleService.setDefaultPayoutAccount(
        customer_identification,
        user.entity_identification,
        method
      );
      const methodName =
        payoutMethodOptions.find((m) => m.method === method)?.title || method;
      toast.success(
        `${methodName} ${t("paymentModule.toasts.defaultAccountSet")}`
      );
      fetchPayoutAccounts();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          t("paymentModule.toasts.defaultAccountSetFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenAddMethod = () => {
    setShowAddMethodModal(true);
    setAddingMethod(null);
  };

  const handleAddMethodContinue = () => {
    if (!addingMethod) return;

    setShowAddMethodModal(false);
    if (addingMethod === PayoutMethod.STRIPE_CONNECT) {
      handleStripeOnboarding();
    } else if (addingMethod === PayoutMethod.BANK_TRANSFER) {
      setIsEditMode(false);
      setBankForm({
        account_holder: "",
        iban: "",
        bic: "",
        bank_name: "",
        country: "",
        bank_address: "",
      });
      setShowBankModal(true);
    } else if (addingMethod === PayoutMethod.CRYPTO) {
      setIsEditMode(false);
      setWalletAddress("");
      setShowCryptoModal(true);
    }
    setAddingMethod(null);
  };

  const handleCloseBankModal = () => {
    setShowBankModal(false);
    setIsEditMode(false);
    setBankForm({
      account_holder: "",
      iban: "",
      bic: "",
      bank_name: "",
      country: "",
      bank_address: "",
    });
  };

  const handleCloseCryptoModal = () => {
    setShowCryptoModal(false);
    setIsEditMode(false);
    setWalletAddress("");
  };

  const existingMethods = accounts.map((a) => a.payout_method);
  const availableMethodsToAdd = payoutMethodOptions.filter(
    (opt) => !existingMethods.includes(opt.method)
  );

  const hasAccounts = accounts.length > 0;

  const sortedAccounts = [...accounts].sort((a, b) => {
    const order = {
      [PayoutMethod.STRIPE_CONNECT]: 1,
      [PayoutMethod.BANK_TRANSFER]: 2,
      [PayoutMethod.CRYPTO]: 3,
    };
    return order[a.payout_method] - order[b.payout_method];
  });

  if (loading) {
    return (
      <div className="payment-module-page">
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center h-64">
            <Spinner size="xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-module-page">
      <Helmet>
        <title>{t("paymentModule.pageTitle")}</title>
        <meta
          name="description"
          content={t("paymentModule.pageDescription") as string}
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/payment-module`}
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("paymentModule.header.title")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.header.description")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasAccounts && (
              <CustomButton
                title={t("paymentModule.feeSimulator.title") as string}
                onClick={() => setShowFeeSimModal(true)}
                variant="secondary"
                size="sm"
              />
            )}
          </div>
        </div>

        {!hasAccounts && (
          <FeatureBenefitsCard
            className="mb-6"
            headerIcon={<HiLink size={18} />}
            title={t("paymentModule.paymentLinksPromo.title")}
            description={t("paymentModule.paymentLinksPromo.description")}
            benefits={paymentLinksBenefits}
          />
        )}

        {!hasAccounts ? (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("paymentModule.accounts.sectionTitle")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("paymentModule.emptyState.question")}
              </p>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                {payoutMethodOptions.map((option) => {
                  const isSelected = selectedMethod === option.method;
                  return (
                    <button
                      key={option.method}
                      onClick={() => handleSelectMethod(option.method)}
                      disabled={
                        !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
                      }
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        isSelected
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex-shrink-0 text-gray-500">
                          {option.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-medium text-gray-900">
                              {option.title}
                            </span>
                            {option.recommended && !option.requiresUpgrade && (
                              <StatusBadge color="success">
                                {t("paymentModule.emptyState.recommended")}
                              </StatusBadge>
                            )}
                            {option.requiresUpgrade && (
                              <UnlockBadge
                                onClick={() => setShowPlansModal(true)}
                              />
                            )}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            {option.description}
                          </span>
                          <span className="mt-1.5 block text-xs text-gray-500">
                            {t("paymentModule.addMethodModal.fee")}:{" "}
                            <span className="font-medium text-gray-700 tabular-nums">
                              {option.feeRate}
                            </span>
                          </span>
                        </span>
                        {isSelected && (
                          <HiCheck
                            size={16}
                            className="mt-0.5 flex-shrink-0 text-blue-600"
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedMethod === PayoutMethod.STRIPE_CONNECT &&
                !isStripeCountryAllowed &&
                ownerCountryCode && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2.5">
                      <HiExclamation
                        size={18}
                        className="mt-0.5 flex-shrink-0 text-amber-600"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-800">
                          {t("paymentModule.warnings.stripeNotAvailable")}{" "}
                          <strong>{ownerCountryName}</strong>
                        </p>
                        <p className="mt-0.5 text-xs text-amber-700">
                          {t(
                            "paymentModule.warnings.stripeNotAvailableDescription"
                          )}{" "}
                          <button
                            onClick={() => setShowCountryWarningModal(true)}
                            className="font-medium text-amber-800 underline hover:text-amber-900"
                          >
                            {t("paymentModule.warnings.profilePage")}
                          </button>{" "}
                          {t("paymentModule.warnings.stripeNotAvailableAction")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {selectedMethod === PayoutMethod.STRIPE_CONNECT &&
                !ownerCountryCode &&
                !isProfileLoading && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2.5">
                      <HiExclamation
                        size={18}
                        className="mt-0.5 flex-shrink-0 text-amber-600"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-800">
                          {t("paymentModule.warnings.countryNotSet")}
                        </p>
                        <p className="mt-0.5 text-xs text-amber-700">
                          {t("paymentModule.warnings.countryNotSetDescription")}{" "}
                          <button
                            onClick={() => setShowCountryWarningModal(true)}
                            className="font-medium text-amber-800 underline hover:text-amber-900"
                          >
                            {t("paymentModule.warnings.profilePage")}
                          </button>{" "}
                          {t("paymentModule.warnings.countryNotSetAction")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {selectedMethod &&
                !(
                  selectedMethod === PayoutMethod.STRIPE_CONNECT &&
                  (!isStripeCountryAllowed || !ownerCountryCode)
                ) && (
                  <div className="mt-4 flex">
                    <CustomButton
                      title={
                        isStripeOnboarding || isSubmitting
                          ? t("paymentModule.addMethodModal.loading")
                          : t("paymentModule.emptyState.continueButton")
                      }
                      onClick={handleContinueSetup}
                      variant="primary"
                      disabled={
                        isStripeOnboarding ||
                        isSubmitting ||
                        !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
                      }
                    />
                  </div>
                )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("paymentModule.accounts.sectionTitle")}
                </h2>
                {availableMethodsToAdd.length > 0 &&
                  permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE) && (
                    <CustomButton
                      title={t("paymentModule.accounts.addMethod") as string}
                      onClick={handleOpenAddMethod}
                      variant="secondary"
                      size="sm"
                      icon="plus"
                      disabled={isSubmitting}
                    />
                  )}
              </div>

              <ul className="divide-y divide-gray-100">
                {sortedAccounts.map((account) => {
                  const methodOption = payoutMethodOptions.find(
                    (opt) => opt.method === account.payout_method
                  );
                  const isStripe =
                    account.payout_method === PayoutMethod.STRIPE_CONNECT;
                  return (
                    <li
                      key={account.payout_method}
                      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start"
                    >
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span className="mt-0.5 flex-shrink-0 text-gray-400">
                          {React.cloneElement(
                            (methodOption?.icon as React.ReactElement) || (
                              <HiCreditCard />
                            ),
                            { size: 18 }
                          )}
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {methodOption?.title || account.payout_method}
                            </span>
                            {(isStripe || account.is_default) && (
                              <StatusBadge color="info">
                                {isStripe
                                  ? t("paymentModule.accounts.default")
                                  : t("paymentModule.accounts.defaultBackup")}
                              </StatusBadge>
                            )}
                            <StatusBadge color={statusTone(account.status)}>
                              {accountStatusLabel(account.status)}
                            </StatusBadge>
                          </div>

                          <p className="mt-0.5 text-xs text-gray-500">
                            {t("paymentModule.accounts.fee")}:{" "}
                            <span className="tabular-nums">
                              {account.fee_rate / 100}%
                              {account.fixed_fee > 0 &&
                                ` + ${(account.fixed_fee / 100).toFixed(2)}`}
                            </span>
                            {account.payout_method === PayoutMethod.CRYPTO &&
                              ` · ${t("paymentModule.accounts.cryptoLimit")}`}
                          </p>
                          {isStripe && (
                            <p className="mt-0.5 text-xs text-gray-500">
                              {t("paymentModule.accounts.stripeFeesNote")}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5 sm:flex-shrink-0">
                        {isStripe && account.status === "PENDING" && (
                          <CustomButton
                            onClick={handleStripeOnboardingRefresh}
                            variant="primary"
                            size="xs"
                            title={
                              isStripeOnboarding
                                ? t("paymentModule.addMethodModal.loading")
                                : t("paymentModule.accounts.continueSetup")
                            }
                            disabled={
                              isStripeOnboarding ||
                              !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
                            }
                          />
                        )}
                        {isStripe &&
                          account.status !== "PENDING" &&
                          account.stripe_dashboard_login_link && (
                            <CustomButton
                              onClick={() =>
                                window.open(
                                  account.stripe_dashboard_login_link,
                                  "_blank"
                                )
                              }
                              variant="secondary"
                              size="xs"
                              title={t(
                                "paymentModule.accounts.accessStripeAccount"
                              )}
                            />
                          )}
                        {!isStripe &&
                          !account.is_default &&
                          accounts.length > 1 &&
                          account.status !== "PENDING" && (
                            <CustomButton
                              onClick={() =>
                                handleSetDefault(account.payout_method)
                              }
                              variant="secondary"
                              size="xs"
                              title={t(
                                "paymentModule.accounts.makeDefaultBackup"
                              )}
                              disabled={
                                isSubmitting ||
                                !permissions.can(
                                  Cap.PAYMENT_CONFIG,
                                  Level.WRITE
                                )
                              }
                            />
                          )}
                        {!isStripe &&
                          permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE) && (
                            <CustomButton
                              onClick={() => handleEditAccount(account)}
                              variant="secondary"
                              size="xs"
                              aria-label={
                                t(
                                  "paymentModule.accounts.editAccount"
                                ) as string
                              }
                              disabled={isSubmitting}
                              comp={
                                <>
                                  <HiPencil size={14} aria-hidden="true" />
                                  <span className="hidden sm:inline">
                                    {t("paymentModule.accounts.editAccount")}
                                  </span>
                                </>
                              }
                            />
                          )}
                        {permissions.can(Cap.PAYMENT_CONFIG, Level.DELETE) && (
                          <CustomButton
                            onClick={() => {
                              setDeletingMethod(account.payout_method);
                              setShowDeleteModal(true);
                            }}
                            variant="danger"
                            size="xs"
                            aria-label={
                              t(
                                "paymentModule.accounts.deleteAccount"
                              ) as string
                            }
                            disabled={isSubmitting}
                            comp={
                              <>
                                <HiTrash size={14} aria-hidden="true" />
                                <span className="hidden sm:inline">
                                  {t("paymentModule.accounts.deleteAccount")}
                                </span>
                              </>
                            }
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t("paymentModule.balance.title")}
                </h2>
                <CustomButton
                  title={
                    isSubmitting
                      ? t("paymentModule.balance.requesting")
                      : t("paymentModule.balance.requestPayout")
                  }
                  onClick={() =>
                    canRequestPayout && setShowRequestPayoutModal(true)
                  }
                  variant={canRequestPayout ? "primary" : "secondary"}
                  size="sm"
                  disabled={
                    isSubmitting ||
                    !canRequestPayout ||
                    !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
                  }
                />
              </div>

              {loadingBalance ? (
                <div className="flex justify-center py-6">
                  <Spinner size="md" />
                </div>
              ) : balance && balance.balances && balance.balances.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {balance.balances.map((currencyBalance, index) => (
                    <li
                      key={index}
                      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3"
                    >
                      <p className="text-sm font-medium text-gray-900 tabular-nums">
                        {formatAmount(
                          currencyBalance.gross_amount / 100,
                          currencyBalance.currency
                        )}
                      </p>
                      <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                        <dt className="text-gray-500">
                          {t("paymentModule.balance.estimatedNet")}
                        </dt>
                        <dd className="text-right text-gray-700 tabular-nums">
                          {formatAmount(
                            currencyBalance.estimated_net_amount / 100,
                            currencyBalance.currency
                          )}
                        </dd>
                        <dt className="text-gray-500">
                          {t("paymentModule.balance.estimatedFee")}
                        </dt>
                        <dd className="text-right text-gray-700 tabular-nums">
                          {formatAmount(
                            currencyBalance.estimated_fee / 100,
                            currencyBalance.currency
                          )}
                        </dd>
                      </dl>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-4 py-6 text-center text-sm text-gray-500">
                  {t("paymentModule.balance.noBalance")}
                </p>
              )}

              {balance && balance.unpaid_payments_count > 0 && (
                <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
                  {balance.unpaid_payments_count}{" "}
                  {balance.unpaid_payments_count !== 1
                    ? t("paymentModule.balance.pendingPaymentsPlural")
                    : t("paymentModule.balance.pendingPayments")}{" "}
                  {t("paymentModule.balance.pendingPayoutSuffix")}
                </p>
              )}

              <div className="border-t border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("paymentModule.payoutHistory.title")}
                </h3>
              </div>

              {loadingPayouts ? (
                <div className="flex justify-center py-6">
                  <Spinner size="md" />
                </div>
              ) : payouts.length > 0 ? (
                <>
                  <ul className="divide-y divide-gray-100 border-t border-gray-100">
                    {payouts.slice(0, 5).map((payout) => {
                      const methodOption = payoutMethodOptions.find(
                        (opt) => opt.method === payout.payout_method
                      );
                      return (
                        <li
                          key={payout.payout_identification}
                          className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 px-4 py-3"
                        >
                          <div className="flex min-w-0 items-start gap-3">
                            <span className="mt-0.5 flex-shrink-0 text-gray-400">
                              {React.cloneElement(
                                (methodOption?.icon as React.ReactElement) || (
                                  <HiCreditCard />
                                ),
                                { size: 18 }
                              )}
                            </span>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 tabular-nums">
                                  {formatAmount(
                                    payout.net_amount / 100,
                                    payout.currency
                                  )}
                                </span>
                                <StatusBadge color={statusTone(payout.status)}>
                                  {payoutStatusLabel(payout.status)}
                                </StatusBadge>
                              </div>
                              <p className="mt-0.5 text-xs text-gray-500">
                                {payout.payout_date_time
                                  ? formatPayModDate(
                                      payout.payout_date_time,
                                      "ll"
                                    )
                                  : formatPayModDate(
                                      payout.creation_date_time,
                                      "ll"
                                    )}
                              </p>
                            </div>
                          </div>

                          <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                            <dt className="text-gray-500">
                              {t("paymentModule.payoutHistory.gross")}
                            </dt>
                            <dd className="text-right text-gray-700 tabular-nums">
                              {formatAmount(
                                payout.gross_amount / 100,
                                payout.currency
                              )}
                            </dd>
                            <dt className="text-gray-500">
                              {t("paymentModule.payoutHistory.fee")}
                            </dt>
                            <dd className="text-right text-gray-700 tabular-nums">
                              {formatAmount(
                                payout.platform_fee / 100,
                                payout.currency
                              )}
                            </dd>
                          </dl>
                        </li>
                      );
                    })}
                  </ul>
                  {payouts.length > 5 && (
                    <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
                      {t("paymentModule.payoutHistory.showing")} 5{" "}
                      {t("paymentModule.payoutHistory.of")} {payouts.length}{" "}
                      {t("paymentModule.payoutHistory.payouts")}
                    </p>
                  )}
                </>
              ) : (
                <p className="px-4 pb-6 text-center text-sm text-gray-500">
                  {t("paymentModule.payoutHistory.noHistory")}
                </p>
              )}
            </section>

            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {t("paymentModule.recurringPlans.title")}
                  </h2>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {t("paymentModule.recurringPlans.noPlansHint")}
                  </p>
                </div>
                {recurringPlans.length > 0 &&
                  permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE) && (
                    <CustomButton
                      title={
                        t(
                          "paymentModule.recurringPlans.createNewPlan"
                        ) as string
                      }
                      onClick={() => setShowCreatePlanModal(true)}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      disabled={isSubmitting}
                    />
                  )}
              </div>

              {loadingPlans ? (
                <div className="flex justify-center py-6">
                  <Spinner size="md" />
                </div>
              ) : recurringPlans.length > 0 ? (
                <ul className="divide-y divide-gray-100">
                  {recurringPlans.map((plan) => {
                    const isMonthly = plan.recurrency === "MONTHLY";
                    return (
                      <li
                        key={plan.plan_code}
                        className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-6"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-medium text-gray-900">
                              {plan.description}
                            </span>
                            <StatusBadge color="gray">
                              {isMonthly
                                ? t("paymentModule.recurringPlans.monthly")
                                : t("paymentModule.recurringPlans.annually")}
                            </StatusBadge>
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {plan.plan_code}
                            {plan.created_at &&
                              ` · ${formatPayModDate(plan.created_at, "ll")}`}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 sm:flex-shrink-0">
                          <p className="text-sm font-medium text-gray-900 tabular-nums">
                            {formatAmount(plan.amount / 100, plan.currency)}
                            <span className="font-normal text-gray-500">
                              {" / "}
                              {isMonthly
                                ? t("paymentModule.recurringPlans.perMonth")
                                : t("paymentModule.recurringPlans.perYear")}
                            </span>
                          </p>
                          <div className="flex items-center gap-1.5">
                            {permissions.can(
                              Cap.PAYMENT_CONFIG,
                              Level.WRITE
                            ) && (
                              <CustomButton
                                onClick={() => {
                                  setEditingPlan({
                                    planCode: plan.plan_code,
                                    description: plan.description,
                                  });
                                  setEditPlanDescription(plan.description);
                                  setEditPlanTouched(false);
                                }}
                                variant="secondary"
                                size="xs"
                                aria-label={
                                  t(
                                    "paymentModule.recurringPlans.editPlan"
                                  ) as string
                                }
                                disabled={isSubmitting}
                                comp={
                                  <>
                                    <HiPencil size={14} aria-hidden="true" />
                                    <span className="hidden sm:inline">
                                      {t(
                                        "paymentModule.recurringPlans.editPlan"
                                      )}
                                    </span>
                                  </>
                                }
                              />
                            )}
                            {permissions.can(
                              Cap.PAYMENT_CONFIG,
                              Level.DELETE
                            ) && (
                              <CustomButton
                                onClick={() => {
                                  setDeletingPlanCode(plan.plan_code);
                                  setShowDeletePlanModal(true);
                                }}
                                variant="danger"
                                size="xs"
                                aria-label={
                                  t(
                                    "paymentModule.recurringPlans.deletePlan"
                                  ) as string
                                }
                                disabled={isSubmitting}
                                comp={
                                  <>
                                    <HiTrash size={14} aria-hidden="true" />
                                    <span className="hidden sm:inline">
                                      {t(
                                        "paymentModule.recurringPlans.deletePlan"
                                      )}
                                    </span>
                                  </>
                                }
                              />
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="flex flex-col items-start gap-3 px-4 py-6">
                  <p className="text-sm text-gray-500">
                    {t("paymentModule.recurringPlans.noPlans")}
                  </p>
                  {permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE) && (
                    <CustomButton
                      title={
                        t("paymentModule.recurringPlans.createAPlan") as string
                      }
                      onClick={() => setShowCreatePlanModal(true)}
                      variant="primary"
                      size="sm"
                      icon="plus"
                      disabled={isSubmitting}
                    />
                  )}
                </div>
              )}
            </section>

            <FeatureBenefitsCard
              headerIcon={<HiLink size={18} />}
              title={t("paymentModule.paymentLinksPromo.title")}
              description={t("paymentModule.paymentLinksPromo.description")}
              benefits={paymentLinksBenefits}
            />
          </div>
        )}
      </div>

      <ModalShell
        show={showBankModal}
        onClose={handleCloseBankModal}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <HiOfficeBuilding
              className="flex-shrink-0 text-blue-600"
              size={24}
            />
            <span className="truncate">
              {isEditMode
                ? t("paymentModule.bankModal.titleEdit")
                : t("paymentModule.bankModal.title")}
            </span>
          </span>
        }
        subtitle={
          isEditMode
            ? t("paymentModule.bankModal.descriptionEdit")
            : t("paymentModule.bankModal.description")
        }
        footer={
          <>
            <CustomButton
              title={t("common.cancel")}
              onClick={handleCloseBankModal}
              variant="secondary"
              disabled={isSubmitting}
            />
            <CustomButton
              title={isEditMode ? t("common.update") : t("common.saveShort")}
              onClick={
                isEditMode ? handleUpdateBankAccount : handleSubmitBankAccount
              }
              variant="primary"
              isLoading={isSubmitting}
              disabled={
                isSubmitting ||
                (!hasAdvancedPayoutAccess && !isEditMode) ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <FieldLabel
              required
              htmlFor="account_holder"
              value={t("paymentModule.bankModal.accountHolder") as string}
            />
            <TextInput
              id="account_holder"
              type="text"
              placeholder={
                t("paymentModule.bankModal.accountHolderPlaceholder") as string
              }
              value={bankForm.account_holder}
              onChange={(e) =>
                handleBankFormChange("account_holder", e.target.value)
              }
              required
              minLength={2}
              maxLength={150}
            />
          </div>

          <div>
            <FieldLabel
              required
              htmlFor="iban"
              value={t("paymentModule.bankModal.iban") as string}
            />
            <TextInput
              id="iban"
              type="text"
              placeholder={
                t("paymentModule.bankModal.ibanPlaceholder") as string
              }
              value={bankForm.iban}
              onChange={(e) => handleBankFormChange("iban", e.target.value)}
              required
              minLength={8}
              maxLength={34}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t("paymentModule.bankModal.ibanHelp")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel
                htmlFor="bic"
                value={t("paymentModule.bankModal.bic") as string}
              />
              <TextInput
                id="bic"
                type="text"
                placeholder={
                  t("paymentModule.bankModal.bicPlaceholder") as string
                }
                value={bankForm.bic}
                onChange={(e) => handleBankFormChange("bic", e.target.value)}
                minLength={6}
                maxLength={15}
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="bank_name"
                value={t("paymentModule.bankModal.bankName") as string}
              />
              <TextInput
                id="bank_name"
                type="text"
                placeholder={
                  t("paymentModule.bankModal.bankNamePlaceholder") as string
                }
                value={bankForm.bank_name}
                onChange={(e) =>
                  handleBankFormChange("bank_name", e.target.value)
                }
                minLength={2}
                maxLength={150}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel
                htmlFor="country"
                value={t("paymentModule.bankModal.country") as string}
              />
              <TextInput
                id="country"
                type="text"
                placeholder={
                  t("paymentModule.bankModal.countryPlaceholder") as string
                }
                value={bankForm.country}
                onChange={(e) =>
                  handleBankFormChange("country", e.target.value)
                }
                minLength={2}
                maxLength={3}
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="bank_address"
                value={t("paymentModule.bankModal.bankAddress") as string}
              />
              <TextInput
                id="bank_address"
                type="text"
                placeholder={
                  t("paymentModule.bankModal.bankAddressPlaceholder") as string
                }
                value={bankForm.bank_address}
                onChange={(e) =>
                  handleBankFormChange("bank_address", e.target.value)
                }
                minLength={5}
                maxLength={200}
              />
            </div>
          </div>

          {!hasAdvancedPayoutAccess && !isEditMode && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <HiExclamation
                  className="mt-0.5 flex-shrink-0 text-amber-600"
                  size={20}
                />
                <p className="text-sm text-amber-800">
                  {t("paymentModule.upgradeRequiredMessage")}
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalShell>

      {/* Crypto Wallet Modal - Fixed to Ethereum/USDC (ERC-20) */}
      <ModalShell
        show={showCryptoModal}
        onClose={handleCloseCryptoModal}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <SiBitcoin className="flex-shrink-0 text-amber-600" size={24} />
            <span className="truncate">
              {isEditMode
                ? t("paymentModule.cryptoModal.titleEdit")
                : t("paymentModule.cryptoModal.title")}
            </span>
          </span>
        }
        subtitle={
          isEditMode
            ? t("paymentModule.cryptoModal.descriptionEdit")
            : t("paymentModule.cryptoModal.description")
        }
        footer={
          <>
            <CustomButton
              title={t("common.cancel")}
              onClick={handleCloseCryptoModal}
              variant="secondary"
              disabled={isSubmitting}
            />
            <CustomButton
              title={
                isSubmitting
                  ? t("paymentModule.cryptoModal.saving")
                  : isEditMode
                  ? t("paymentModule.cryptoModal.update")
                  : t("paymentModule.cryptoModal.save")
              }
              onClick={
                isEditMode
                  ? handleUpdateCryptoAccount
                  : handleSubmitCryptoAccount
              }
              variant="primary"
              disabled={
                isSubmitting ||
                (!hasAdvancedPayoutAccess && !isEditMode) ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500">
                  {t("paymentModule.cryptoModal.network")}
                </span>
                <p className="text-sm font-semibold text-gray-900">
                  {t("paymentModule.cryptoModal.networkValue")}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">
                  {t("paymentModule.cryptoModal.currency")}
                </span>
                <p className="text-sm font-semibold text-gray-900">
                  {t("paymentModule.cryptoModal.currencyValue")}
                </p>
              </div>
            </div>
          </div>

          <div>
            <FieldLabel
              required
              htmlFor="wallet_address"
              value={t("paymentModule.cryptoModal.walletAddress") as string}
            />
            <TextInput
              id="wallet_address"
              type="text"
              placeholder={
                t(
                  "paymentModule.cryptoModal.walletAddressPlaceholder"
                ) as string
              }
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              required
              minLength={20}
              maxLength={150}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t("paymentModule.cryptoModal.walletAddressHelp")}
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-amber-600 flex-shrink-0 mt-0.5">
                <HiExclamation size={20} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-amber-800">
                  {t("paymentModule.cryptoModal.warningTitle")}
                </h4>
                <p className="text-xs text-amber-700 mt-1">
                  {t("paymentModule.cryptoModal.warningMessage")}
                </p>
              </div>
            </div>
          </div>

          {!hasAdvancedPayoutAccess && !isEditMode && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <HiExclamation
                  className="mt-0.5 flex-shrink-0 text-amber-600"
                  size={20}
                />
                <p className="text-sm text-amber-800">
                  {t("paymentModule.upgradeRequiredMessage")}
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalShell>

      <ModalShell
        show={showCountryWarningModal}
        onClose={() => setShowCountryWarningModal(false)}
        footer={
          <>
            <CustomButton
              onClick={() => setShowCountryWarningModal(false)}
              title={t("common.cancel")}
            />
            <CustomButton
              variant="primary"
              onClick={() => {
                setShowCountryWarningModal(false);
                navigate("/control-panel/profile");
              }}
              title={t("paymentModule.countryWarningModal.updateButton")}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <HiExclamation size={24} className="text-amber-500" />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("paymentModule.countryWarningModal.title")}
            </h2>
            <div className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.countryWarningModal.description")}{" "}
              <strong>{t("paymentModule.countryWarningModal.minutes")}</strong>{" "}
              {t("paymentModule.countryWarningModal.description2")}
            </div>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingMethod(null);
        }}
        footer={
          <>
            <CustomButton
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingMethod(null);
              }}
              title={t("common.cancel")}
              disabled={isSubmitting}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteAccount}
              title={
                isSubmitting
                  ? t("paymentModule.deleteModal.deleting")
                  : t("paymentModule.deleteModal.delete")
              }
              disabled={
                isSubmitting ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.DELETE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <HiTrash size={24} className="text-red-500" />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("paymentModule.deleteModal.title")}
            </h2>
            <div className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.deleteModal.description")}{" "}
              <strong>
                {payoutMethodOptions.find((m) => m.method === deletingMethod)
                  ?.title || deletingMethod}
              </strong>{" "}
              {t("paymentModule.deleteModal.description2")}
            </div>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={showDeletePlanModal}
        onClose={() => {
          setShowDeletePlanModal(false);
          setDeletingPlanCode(null);
        }}
        footer={
          <>
            <CustomButton
              onClick={() => {
                setShowDeletePlanModal(false);
                setDeletingPlanCode(null);
              }}
              title={t("common.cancel") as string}
              disabled={isDeletingPlan}
            />
            <CustomButton
              variant="danger"
              onClick={handleDeleteRecurringPlan}
              title={
                isDeletingPlan
                  ? (t("paymentModule.deletePlanModal.deleting") as string)
                  : (t("common.delete") as string)
              }
              disabled={
                isDeletingPlan ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.DELETE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <HiTrash size={24} className="text-red-500" />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("paymentModule.deletePlanModal.title")}
            </h2>
            <div className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.deletePlanModal.confirmStart")}{" "}
              <strong>
                {recurringPlans.find((p) => p.plan_code === deletingPlanCode)
                  ?.description || deletingPlanCode}
              </strong>
              ?<br /> {t("paymentModule.deletePlanModal.confirmEnd")}
            </div>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={!!editingPlan}
        onClose={() => {
          setEditingPlan(null);
          setEditPlanTouched(false);
        }}
        footer={
          <>
            <CustomButton
              onClick={() => {
                setEditingPlan(null);
                setEditPlanTouched(false);
              }}
              title={t("common.cancel") as string}
              disabled={isEditingPlan}
            />
            <CustomButton
              variant="primary"
              onClick={handleUpdateRecurringPlan}
              title={
                isEditingPlan
                  ? (t("paymentModule.editPlanModal.saving") as string)
                  : (t("common.saveShort") as string)
              }
              disabled={
                isEditingPlan ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
            <HiPencil size={24} className="text-blue-500" />
          </div>

          <div className="mb-4 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("paymentModule.editPlanModal.title")}
            </h2>
            <div className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.editPlanModal.descriptionStart")}{" "}
              <strong>{editingPlan?.planCode}</strong>.
            </div>
          </div>

          <div>
            <FieldLabel
              htmlFor="editPlanDescription"
              value={
                t("paymentModule.editPlanModal.descriptionLabel") as string
              }
              className="mb-2 block"
            />
            <p className="mb-2 text-xs text-gray-500">
              {t("paymentModule.editPlanModal.descriptionPrivateNotice")}
            </p>
            <TextInput
              id="editPlanDescription"
              value={editPlanDescription}
              onChange={(e) => setEditPlanDescription(e.target.value)}
              placeholder={
                t(
                  "paymentModule.editPlanModal.descriptionPlaceholder"
                ) as string
              }
              maxLength={150}
              color={
                editPlanTouched &&
                (!editPlanDescription.trim() ||
                  editPlanDescription.trim().length > 150)
                  ? "failure"
                  : undefined
              }
            />
            {editPlanTouched && !editPlanDescription.trim() && (
              <p className="mt-1 text-xs text-red-600">
                {t("paymentModule.editPlanModal.descriptionRequired")}
              </p>
            )}
            {editPlanTouched && editPlanDescription.trim().length > 150 && (
              <p className="mt-1 text-xs text-red-600">
                {t("paymentModule.editPlanModal.descriptionTooLong")}
              </p>
            )}
            <p className="text-gray-400 text-xs mt-1 text-right">
              {editPlanDescription.trim().length}/150
            </p>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={showAddMethodModal}
        onClose={() => {
          setShowAddMethodModal(false);
          setAddingMethod(null);
        }}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <HiPlus className="flex-shrink-0 text-blue-600" size={24} />
            <span className="truncate">
              {t("paymentModule.addMethodModal.title")}
            </span>
          </span>
        }
        subtitle={t("paymentModule.addMethodModal.description")}
        footer={
          <>
            <CustomButton
              title={t("common.cancel")}
              onClick={() => {
                setShowAddMethodModal(false);
                setAddingMethod(null);
              }}
              variant="secondary"
              disabled={isSubmitting}
            />
            <CustomButton
              title={
                isStripeOnboarding || isSubmitting
                  ? t("paymentModule.addMethodModal.loading")
                  : t("paymentModule.addMethodModal.continue")
              }
              onClick={handleAddMethodContinue}
              variant="primary"
              disabled={
                isStripeOnboarding ||
                isSubmitting ||
                !addingMethod ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="space-y-4">
          {availableMethodsToAdd.length > 0 ? (
            <div className="space-y-3">
              {availableMethodsToAdd.map((option) => {
                const isStripeUnavailable =
                  option.method === PayoutMethod.STRIPE_CONNECT &&
                  (!isStripeCountryAllowed || !ownerCountryCode);

                return (
                  <button
                    key={option.method}
                    onClick={() =>
                      !isStripeUnavailable && setAddingMethod(option.method)
                    }
                    disabled={isStripeUnavailable}
                    className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                      addingMethod === option.method
                        ? "border-blue-500 bg-blue-50"
                        : isStripeUnavailable
                        ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className="mt-0.5 flex-shrink-0 text-gray-500">
                      {option.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">
                          {option.title}
                        </span>
                        {option.requiresUpgrade && (
                          <UnlockBadge
                            onClick={() => setShowPlansModal(true)}
                          />
                        )}
                      </span>
                      <span className="mt-1 block text-xs text-gray-500">
                        {option.description}
                      </span>
                      <span className="mt-1.5 block text-xs text-gray-500">
                        {t("paymentModule.addMethodModal.fee")}:{" "}
                        <span className="font-medium text-gray-700 tabular-nums">
                          {option.feeRate}
                        </span>
                      </span>
                      {isStripeUnavailable && (
                        <span className="mt-1 block text-xs text-red-600">
                          {t("paymentModule.addMethodModal.notAvailable")}
                        </span>
                      )}
                      {option.requiresUpgrade && (
                        <span className="mt-1 block text-xs text-amber-700">
                          {t("paymentModule.upgradeRequiredMessage")}
                        </span>
                      )}
                    </span>
                    {addingMethod === option.method && (
                      <HiCheck
                        size={16}
                        className="mt-0.5 flex-shrink-0 text-blue-600"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{t("paymentModule.addMethodModal.noMethods")}</p>
            </div>
          )}
        </div>
      </ModalShell>

      <ModalShell
        show={showRequestPayoutModal}
        onClose={() => setShowRequestPayoutModal(false)}
        footer={
          <>
            <CustomButton
              onClick={() => setShowRequestPayoutModal(false)}
              title={t("common.cancel")}
              disabled={isSubmitting}
            />
            <CustomButton
              variant="primary"
              onClick={handleRequestPayout}
              title={
                isSubmitting
                  ? t("paymentModule.requestPayoutModal.processing")
                  : t("paymentModule.requestPayoutModal.confirm")
              }
              disabled={
                isSubmitting ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
            <HiCurrencyDollar size={24} className="text-green-600" />
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("paymentModule.requestPayoutModal.title")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("paymentModule.requestPayoutModal.description")}
            </p>

            {balance && balance.balances && balance.balances.length > 0 && (
              <dl className="mt-3 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                {balance.balances.map((currencyBalance, index) => (
                  <React.Fragment key={index}>
                    <dt className="text-gray-500">
                      {currencyBalance.currency}
                    </dt>
                    <dd className="text-right text-gray-700 tabular-nums">
                      {formatAmount(
                        currencyBalance.estimated_net_amount / 100,
                        currencyBalance.currency
                      )}{" "}
                      <span className="text-gray-400">
                        {t("paymentModule.requestPayoutModal.afterFees")}
                      </span>
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            )}

            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <HiExclamation
                  className="mt-0.5 flex-shrink-0 text-amber-600"
                  size={18}
                />
                <p className="text-xs text-amber-800">
                  {t("paymentModule.requestPayoutModal.frequencyLimit")}
                </p>
              </div>
            </div>

            <p className="mt-3 text-xs text-gray-500">
              {t("paymentModule.requestPayoutModal.note")}
            </p>
          </div>
        </div>
      </ModalShell>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        title={t("paymentModule.upgradeModalTitle") as string}
        targetPlan={PlanTier.LARGE_BUSINESS}
      />

      <ModalShell
        show={showFeeSimModal}
        onClose={() => setShowFeeSimModal(false)}
        size="lg"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <HiChartBar className="flex-shrink-0 text-blue-600" size={24} />
            <span className="truncate">
              {t("paymentModule.feeSimulator.title")}
            </span>
          </span>
        }
        subtitle={t("paymentModule.feeSimulator.description")}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <FieldLabel
                htmlFor="fee-method"
                value={
                  t("paymentModule.feeSimulator.payoutMethodLabel") as string
                }
              />
              <Select
                id="fee-method"
                value={feeSimMethod}
                onChange={(e) => {
                  setFeeSimMethod(e.target.value);
                  setFeeResult(null);
                }}
              >
                <option value="STRIPE_CONNECT">
                  {t("paymentModule.feeSimulator.stripeConnect")}
                </option>
                <option value="BANK_TRANSFER">
                  {t("paymentModule.feeSimulator.bankTransfer")}
                </option>
                <option value="CRYPTO">
                  {t("paymentModule.feeSimulator.crypto")}
                </option>
              </Select>
            </div>
            <div>
              <FieldLabel
                htmlFor="fee-amount"
                value={t("paymentModule.feeSimulator.amountLabel") as string}
              />
              <TextInput
                id="fee-amount"
                type="number"
                placeholder={
                  t("paymentModule.feeSimulator.amountPlaceholder") as string
                }
                value={feeSimAmount}
                onChange={(e) => setFeeSimAmount(e.target.value)}
                min={1}
                max={100000}
                step={0.01}
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="fee-currency"
                value={t("paymentModule.feeSimulator.currencyLabel") as string}
              />
              <Select
                id="fee-currency"
                value={feeSimCurrency}
                onChange={(e) => setFeeSimCurrency(e.target.value)}
              >
                <option value="CHF">CHF</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </Select>
            </div>
          </div>

          <CustomButton
            title={
              loadingFees
                ? (t("paymentModule.feeSimulator.calculating") as string)
                : (t("paymentModule.feeSimulator.calculate") as string)
            }
            onClick={handleCalculateFees}
            variant="primary"
            className="w-full"
            disabled={loadingFees || !feeSimAmount}
          />

          {loadingFees ? (
            <div className="flex justify-center py-4">
              <Spinner size="md" />
            </div>
          ) : feeResult ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <dl className="grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-1.5 text-xs">
                <dt className="text-gray-500">
                  {t("paymentModule.feeSimulator.payoutAmount")}
                </dt>
                <dd className="text-right font-medium text-gray-900 tabular-nums">
                  {formatAmount(feeResult.amount / 100, feeResult.currency)}
                </dd>

                <dt className="text-gray-500">
                  {t("paymentModule.feeSimulator.feeRate")}
                </dt>
                <dd className="text-right text-gray-700">
                  {feeResult.fee_description}
                </dd>

                <dt className="text-gray-500">
                  {t("paymentModule.feeSimulator.percentageFee")}
                </dt>
                <dd className="text-right text-gray-700 tabular-nums">
                  -
                  {formatAmount(
                    feeResult.percentage_fee_cents / 100,
                    feeResult.currency
                  )}
                </dd>

                {feeResult.fixed_fee_cents > 0 && (
                  <>
                    <dt className="text-gray-500">
                      {t("paymentModule.feeSimulator.fixedFee")}
                    </dt>
                    <dd className="text-right text-gray-700 tabular-nums">
                      -
                      {formatAmount(
                        feeResult.fixed_fee_cents / 100,
                        feeResult.currency
                      )}
                    </dd>
                  </>
                )}

                <dt className="mt-1.5 border-t border-gray-200 pt-2 text-gray-500">
                  {t("paymentModule.feeSimulator.totalFee")} (
                  {typeof feeResult.equivalent_percent === "number"
                    ? feeResult.equivalent_percent.toFixed(2)
                    : feeResult.equivalent_percent}
                  %)
                </dt>
                <dd className="mt-1.5 border-t border-gray-200 pt-2 text-right font-medium text-red-700 tabular-nums">
                  -
                  {formatAmount(
                    feeResult.total_fee_cents / 100,
                    feeResult.currency
                  )}
                </dd>

                <dt className="text-sm font-medium text-gray-900">
                  {t("paymentModule.feeSimulator.youReceive")}
                </dt>
                <dd className="text-right text-sm font-medium text-gray-900 tabular-nums">
                  {formatAmount(
                    feeResult.net_amount_cents / 100,
                    feeResult.currency
                  )}
                </dd>
              </dl>
            </div>
          ) : (
            <p className="py-6 text-center text-xs text-gray-500">
              {t("paymentModule.feeSimulator.emptyHint")}
            </p>
          )}
        </div>
      </ModalShell>

      <ModalShell
        show={showCreatePlanModal}
        onClose={() => {
          setShowCreatePlanModal(false);
          setPlanForm({
            amount: "",
            currency: "USD",
            recurrency: "MONTHLY",
            description: "",
          });
        }}
        size="md"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <HiCreditCard className="flex-shrink-0 text-blue-600" size={24} />
            <span className="truncate">
              {t("paymentModule.createPlanModal.title")}
            </span>
          </span>
        }
        footer={
          <>
            <CustomButton
              title={t("common.cancel") as string}
              onClick={() => {
                setShowCreatePlanModal(false);
                setPlanForm({
                  amount: "",
                  currency: "USD",
                  recurrency: "MONTHLY",
                  description: "",
                });
                setPlanFormTouched({ description: false, amount: false });
              }}
              variant="secondary"
            />
            <CustomButton
              title={
                isSubmitting
                  ? (t("paymentModule.createPlanModal.creating") as string)
                  : (t("paymentModule.createPlanModal.createPlan") as string)
              }
              onClick={handleCreateRecurringPlan}
              variant="primary"
              disabled={
                isSubmitting ||
                !permissions.can(Cap.PAYMENT_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <FieldLabel
              htmlFor="plan-description"
              value={
                t("paymentModule.createPlanModal.descriptionLabel") as string
              }
            />
            <p className="mb-2 mt-1 text-xs text-gray-500">
              {t("paymentModule.createPlanModal.descriptionPrivateNotice")}
            </p>
            <TextInput
              id="plan-description"
              placeholder={
                t(
                  "paymentModule.createPlanModal.descriptionPlaceholder"
                ) as string
              }
              value={planForm.description}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              maxLength={150}
              color={
                planFormTouched.description &&
                (!planForm.description || planForm.description.length > 150)
                  ? "failure"
                  : undefined
              }
            />
            {planFormTouched.description && !planForm.description ? (
              <p className="mt-1 text-xs text-red-600">
                {t("paymentModule.createPlanModal.descriptionRequired")}
              </p>
            ) : planFormTouched.description &&
              planForm.description.length > 150 ? (
              <p className="mt-1 text-xs text-red-600">
                {t("paymentModule.createPlanModal.descriptionTooLong")}
              </p>
            ) : (
              <p className="mt-1 text-right text-xs text-gray-500">
                {planForm.description.length}/150
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel
                htmlFor="plan-amount"
                value={t("paymentModule.createPlanModal.amountLabel") as string}
              />
              <TextInput
                id="plan-amount"
                type="number"
                placeholder={
                  t("paymentModule.createPlanModal.amountPlaceholder") as string
                }
                value={planForm.amount}
                onChange={(e) =>
                  setPlanForm((prev) => ({ ...prev, amount: e.target.value }))
                }
                min={1}
                max={10000}
                step={0.01}
                color={
                  planFormTouched.amount &&
                  (!planForm.amount ||
                    isNaN(parseFloat(planForm.amount)) ||
                    parseFloat(planForm.amount) < 1 ||
                    parseFloat(planForm.amount) > 10000)
                    ? "failure"
                    : undefined
                }
              />
              {planFormTouched.amount && !planForm.amount ? (
                <p className="mt-1 text-xs text-red-600">
                  {t("paymentModule.createPlanModal.amountRequired")}
                </p>
              ) : planFormTouched.amount &&
                planForm.amount &&
                (parseFloat(planForm.amount) < 1 ||
                  parseFloat(planForm.amount) > 10000) ? (
                <p className="mt-1 text-xs text-red-600">
                  {t("paymentModule.createPlanModal.amountRange")}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  {t("paymentModule.createPlanModal.amountHint")}
                </p>
              )}
            </div>
            <div>
              <FieldLabel
                htmlFor="plan-currency"
                value={
                  t("paymentModule.createPlanModal.currencyLabel") as string
                }
              />
              <Select
                id="plan-currency"
                value={planForm.currency}
                onChange={(e) =>
                  setPlanForm((prev) => ({
                    ...prev,
                    currency: e.target.value as PaymentRequestCurrencyEnum,
                  }))
                }
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="CHF">CHF</option>
              </Select>
            </div>
          </div>

          <div>
            <FieldLabel
              htmlFor="plan-recurrency"
              value={
                t("paymentModule.createPlanModal.recurrencyLabel") as string
              }
            />
            <Select
              id="plan-recurrency"
              value={planForm.recurrency}
              onChange={(e) =>
                setPlanForm((prev) => ({
                  ...prev,
                  recurrency: e.target.value as RecurrencyEnum,
                }))
              }
            >
              <option value="MONTHLY">
                {t("paymentModule.createPlanModal.byMonth")}
              </option>
              <option value="ANNUALLY">
                {t("paymentModule.createPlanModal.byYear")}
              </option>
            </Select>
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default PaymentModule;
